import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import { switchMap, map, catchError, throwError, from, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ReportTemplateApiService {
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);
  protected http = inject(HttpClient);
  constructor() {}

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.reportTemplateList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.reportTemplate.clear().then(() =>
                appDb.reportTemplate.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    isSynced: 1,
                    isCreated: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.reportTemplate.bulkPut(
              data.items.map((item) => ({
                ...item,
                isSynced: 1,
                isCreated: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }
  create(data) {
    return this.http.post(generateURL(apiPaths.reportTemplate), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'reportTemplateKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('reportTemplateKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  update(id, data) {
    return this.http
      .put(generateURL(apiPaths.reportTemplateId, { id }), data)
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'reportTemplateKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('reportTemplateKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
  delete(id) {
    return this.http
      .delete(generateURL(apiPaths.reportTemplateId, { id }))
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'reportTemplateKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('reportTemplateKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
}
